import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Paper,
  InputAdornment,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';

// Services
import proposalsServices from '../../services/proposalsServices';
import paymentMethodsServices from '../../services/paymentMethodsServices';
import paymentServices from '../../services/paymentServices';

// Context
import { AuthContext } from '../../context/AuthProvider';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const STATUS_COLORS = { completed: 'success', submitted: 'warning', pending: 'warning', reversed: 'default', failed: 'error', refunded: 'info' };

/**
 * Payment records modal — backed by the payment ledger (payment table).
 * Rows are immutable: mistakes are reversed (kept in history), never edited
 * or deleted. Square online payments appear here automatically via webhooks.
 * The "paid in full" flag + comment still live in proposal.additionalNotes.
 */
const PaymentModal = ({ open, onClose, proposalId, proposal, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userData } = useContext(AuthContext);
  const isAdmin = userData?.role === 'superadmin' || userData?.role === 'admin';
  const canPay = userData?.permissions?.includes('payments');
  // Non-admins (techs/salesmen) may always SUBMIT payment info; posting real
  // money (admins) still requires the payments permission.
  const canAdd = isAdmin ? canPay : true;

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [expectedMethod, setExpectedMethod] = useState('');
  const [saving, setSaving] = useState(false);

  // New payment form
  const [adding, setAdding] = useState(false);
  const [newPayment, setNewPayment] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    paymentMethodsServices
      .getActive()
      .then((res) => setPaymentMethods(res.data || []))
      .catch((e) => console.log('Error loading payment methods:', e));
  }, []);

  const loadLedger = useCallback(async () => {
    if (!proposalId) return;
    setLoading(true);
    try {
      const res = await paymentServices.getLedger(proposalId);
      setPayments(res.data.payments || []);
      setBalance(res.data.balance || null);
    } catch (e) {
      console.log('Error loading ledger:', e);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  useEffect(() => {
    if (open) {
      loadLedger();
      setAdding(false);
      setNewPayment(null);
    }
  }, [open, loadLedger]);

  useEffect(() => {
    if (proposal) {
      setComment(proposal.additionalNotes?.payment?.comment || '');
      setExpectedMethod(proposal.additionalNotes?.payment?.expectedMethod || '');
    }
  }, [proposal]);

  const startAdd = () => {
    setEditingId(null);
    setNewPayment({
      methodId: paymentMethods[0]?.id || '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      reference: '',
      notes: '',
      isDeposit: false,
    });
    setAdding(true);
  };

  // A submitted (unconfirmed) payment is information, not money — the author
  // (or an admin) may correct mistakes in place until accounting confirms it.
  const startEdit = (p) => {
    setEditingId(p.id);
    setNewPayment({
      methodId: p.methodId || '',
      amount: String(p.amount ?? ''),
      date: p.paidAt ? new Date(p.paidAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      reference: p.checkNumber || p.reference || '',
      notes: p.notes || '',
      isDeposit: !!p.isDeposit,
    });
    setAdding(true);
  };

  const submitAdd = async () => {
    const selMethod = paymentMethods.find((m) => m.id === newPayment.methodId);
    const isNoPayment = (selMethod?.name || '') === 'No payment required';
    // "No payment required" (zero-price warranty/fix jobs) carries no amount —
    // the server records $0 and the job closes as paid.
    const amount = isNoPayment ? 0 : parseFloat(newPayment.amount);
    if (!isNoPayment && (isNaN(amount) || amount <= 0)) {
      toast.error('Enter the payment amount');
      return;
    }
    const contractTotal = balance?.contractPrice || 0;
    if (contractTotal > 0 && amount > contractTotal * 1.5) {
      // Guard against fat-finger entries (e.g. a confirmation number typed
      // into the amount field).
      // eslint-disable-next-line no-alert
      const proceed = window.confirm(
        `This payment (${fmt(amount)}) is much higher than the contract total (${fmt(contractTotal)}).\n\nSave anyway?`,
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const method = paymentMethods.find((m) => m.id === newPayment.methodId);
      const isCheck = (method?.name || '').toLowerCase() === 'check';
      const payload = {
        amount,
        methodId: newPayment.methodId || undefined,
        paidAt: newPayment.date,
        checkNumber: isCheck ? newPayment.reference || '' : '',
        reference: !isCheck ? newPayment.reference || '' : '',
        notes: newPayment.notes || '',
        isDeposit: newPayment.isDeposit,
      };
      const res = editingId
        ? await paymentServices.updatePayment(editingId, payload)
        : await paymentServices.recordPayment(proposalId, payload);
      toast.success(editingId
        ? 'Payment information updated'
        : isAdmin ? 'Payment recorded' : 'Payment information sent — awaiting accounting confirmation');
      if (!isNoPayment && res.data?.balance?.balance <= 0) {
        toast.info('Job is now fully paid — any open Square invoice is being canceled automatically');
      }
      setAdding(false);
      setNewPayment(null);
      setEditingId(null);
      await loadLedger();
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleReverse = async (payment) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Reverse this ${fmt(payment.amount)} payment? It stays in history as reversed.\nReason:`,
      '',
    );
    if (reason === null) return;
    try {
      await paymentServices.reversePayment(payment.id, reason || 'reversed by employee');
      toast.success('Payment reversed');
      await loadLedger();
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to reverse payment');
    }
  };

  // Accounting confirms a tech-submitted payment: it becomes real money.
  const handleConfirm = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Confirm and record this payment: ${fmt(p.amount)}?`)) return;
    try {
      const res = await paymentServices.confirmPayment(p.id);
      toast.success('Payment recorded');
      if (res.data?.balance?.balance <= 0) {
        toast.info('Job is now fully paid — any open Square invoice is being canceled automatically');
      }
      await loadLedger();
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to confirm payment');
    }
  };

  // Comment only: the "Paid in full" flag is maintained by the server from
  // the payment ledger (it flips when the balance reaches zero).
  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      const updateData = {
        additionalNotes: {
          ...proposal.additionalNotes,
          payment: { ...(proposal.additionalNotes?.payment || {}), comment, expectedMethod },
        },
      };
      await proposalsServices.update(proposalId, updateData);
      toast.success('Payment notes saved');
      refresh && refresh();
      onClose();
    } catch (e) {
      toast.error('Failed to save');
      console.log(e);
    } finally {
      setSaving(false);
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '95%' : 520,
    maxWidth: '95vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: isMobile ? 2 : 3,
    overflow: 'auto',
  };

  const activePayments = payments.filter((p) => p.status !== 'reversed' && p.status !== 'failed');

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon sx={{ color: '#0d3c61' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3c61' }}>
              Payment Information
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.2 }}>
              Balance
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: (balance?.balance ?? 0) <= 0 ? '#2e7d32' : '#d32f2f', lineHeight: 1.2 }}
            >
              {fmt(balance?.balance)}
            </Typography>
            {balance?.pendingTotal > 0 && (
              <Typography variant="caption" sx={{ color: '#ed6c02' }}>
                + {fmt(balance.pendingTotal)} pending ACH
              </Typography>
            )}
          </Box>
        </Box>

        {/* Ledger */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
              Payment Records
            </Typography>
            {canAdd && !adding && (
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={startAdd} sx={{ textTransform: 'none', fontSize: 12 }}>
                Add
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
          ) : payments.length === 0 && !adding ? (
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', color: '#999', bgcolor: '#fafafa' }}>
              <Typography variant="body2">No payments recorded</Typography>
            </Paper>
          ) : (
            payments.map((p) => (
              <Paper
                key={p.id}
                variant="outlined"
                sx={{
                  p: 1.25, mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                  opacity: p.status === 'reversed' || p.status === 'failed' ? 0.55 : 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 140 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: p.status === 'reversed' ? 'line-through' : 'none' }}>
                    {(p.method?.name || paymentMethods.find((m) => m.id === p.methodId)?.name) === 'No payment required'
                      ? 'No payment required'
                      : `${fmt(p.amount)} · ${p.method?.name || paymentMethods.find((m) => m.id === p.methodId)?.name || '—'}`}
                    {p.isDeposit ? ' · deposit' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : ''}
                    {p.checkNumber ? ` · CK#${p.checkNumber}` : ''}
                    {p.reference && !p.checkNumber ? ` · ${p.reference}` : ''}
                    {p.source === 'square' ? ' · Square' : ''}
                    {p.squareFee != null ? ` · fee ${fmt(p.squareFee)}` : ''}
                    {p.notes ? ` · ${p.notes}` : ''}
                  </Typography>
                </Box>
                <Chip size="small" label={p.status} color={STATUS_COLORS[p.status] || 'default'} />
                {p.status === 'submitted' && (isAdmin ? canPay : p.recordedById === userData?.id) && (
                  <Tooltip title="Edit this submitted payment (not confirmed yet)">
                    <IconButton size="small" onClick={() => startEdit(p)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canPay && isAdmin && p.status === 'submitted' && (
                  <Button size="small" variant="contained" color="success" onClick={() => handleConfirm(p)}>
                    Record Payment
                  </Button>
                )}
                {!isAdmin && p.status === 'submitted' && p.recordedById === userData?.id && (
                  <Tooltip title="Cancel this submission">
                    <IconButton size="small" onClick={() => handleReverse(p)}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canPay && isAdmin && (p.status === 'completed' || p.status === 'pending' || p.status === 'submitted') && p.source !== 'square' && (
                  <Tooltip title="Reverse payment (kept in history)">
                    <IconButton size="small" onClick={() => handleReverse(p)}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Paper>
            ))
          )}

          {/* Add form */}
          {adding && newPayment && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: '#f8fbff' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <TextField
                  label="Amount" size="small" sx={{ width: 130 }} autoFocus
                  disabled={(paymentMethods.find((m) => m.id === newPayment.methodId)?.name || '') === 'No payment required'}
                  value={(paymentMethods.find((m) => m.id === newPayment.methodId)?.name || '') === 'No payment required' ? '' : newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
                <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                  <Select
                    value={newPayment.methodId}
                    onChange={(e) => setNewPayment({ ...newPayment, methodId: e.target.value })}
                  >
                    {paymentMethods
                      .filter((m) => m.name !== 'No payment required' || (balance?.contractPrice || 0) <= 0)
                      .map((m) => (
                        <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Date" type="date" size="small" sx={{ width: 150 }}
                  value={newPayment.date}
                  onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <TextField
                  label="Check # / reference" size="small" sx={{ flex: 1, minWidth: 140 }}
                  value={newPayment.reference}
                  onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                />
                <TextField
                  label="Notes" size="small" sx={{ flex: 2, minWidth: 160 }}
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={newPayment.isDeposit}
                      onChange={(e) => setNewPayment({ ...newPayment, isDeposit: e.target.checked })}
                    />
                  )}
                  label={<Typography variant="body2">Deposit</Typography>}
                />
                <Box>
                  <Button size="small" onClick={() => { setAdding(false); setEditingId(null); }} sx={{ mr: 1 }}>Cancel</Button>
                  <Button size="small" variant="contained" disabled={saving} onClick={submitAdd}>
                    {saving ? 'Saving…' : editingId ? 'Save Changes' : isAdmin ? 'Record payment' : 'Send Payment Information'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {activePayments.length > 0 && balance && (
            <Typography variant="caption" color="text.secondary">
              Total received: {fmt(balance.completedTotal)} of {fmt(balance.contractPrice)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Paid in full is an automatic indicator: it turns on when the
            recorded payments cover the contract total (balance = 0). */}
        <Box sx={{ my: 1 }}>
          {balance && (balance.contractPrice > 0
            ? balance.balance <= 0.005
            : payments.some((p) => p.status === 'completed')) ? (
            <Chip color="success" label="Paid in full" sx={{ fontWeight: 600 }} />
          ) : (
            <Chip
              color="warning"
              variant="outlined"
              label={`Not paid in full — outstanding ${fmt(balance?.balance ?? 0)}`}
            />
          )}
        </Box>
        {/* A note about how the client PLANS to pay — not a payment record.
            Received money goes in via Add Payment only. */}
        <TextField
          select label="Expected payment method" fullWidth size="small" sx={{ mt: 1 }}
          value={expectedMethod}
          onChange={(e) => setExpectedMethod(e.target.value)}
          helperText="Note only — how the client plans to pay. Record received money with Add Payment."
        >
          <MenuItem value="">—</MenuItem>
          {paymentMethods
            .filter((m) => m.name !== 'No payment required' || (balance?.contractPrice || 0) <= 0)
            .map((m) => (
              <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>
            ))}
        </TextField>
        <TextField
          label="Payment comment" fullWidth size="small" multiline minRows={2} sx={{ mt: 1, mb: 2 }}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" disabled={saving} onClick={handleSaveNotes}>
            {saving ? 'Saving…' : 'Save Notes'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PaymentModal;
